import React, { useState, useCallback, useEffect } from 'react';
import { IoClose, IoLocate } from 'react-icons/io5';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../../firebase';
import UnifiedMapView from '../../../components/map/UnifiedMapView';
import { TREASURE_HUNT_ZOOM } from '../hooks/useTreasureHuntMode';

import { useTranslation } from 'react-i18next';
import SPOT_MESSAGES from '../constants/SpotMessages';
import { GIFTBOX_HUB_KEYS, DAILY_NORMAL_LIMIT, DAILY_LANDMARK_LIMIT } from '../giftbox/constants';
import { useGiftBoxInventory } from '../giftbox/useGiftBoxInventory';
import { getKSTTodayStr } from '../../../utils/dateUtils';
import { useAppContext } from '../../../contexts/AppContext';

const TreasureHuntOverlay = ({
    onClose,
    myPos,
    spots,
    pointSpots,
    useKakaoMap,
    onToggleMap,
    mapTarget,
    setMapTarget,
    distanceRadius,
    onOpenDetail,
    onPointSpotClick,
    onPointSpotClaimed,
    piUser,
    environment
}) => {
    const { t } = useTranslation();
    // 🎯 [Fix] Get 'opened today' count from Context to match GiftBoxHub logic
    const { pointSpotOpenedToday } = useAppContext();

    const { inventory, refresh: refreshInventory } = useGiftBoxInventory(piUser?.uid, environment, { enabled: !!piUser?.uid && !!environment });
    const [distanceMessage, setDistanceMessage] = useState('');
    const [collectToast, setCollectToast] = useState('');
    const [isCollecting, setIsCollecting] = useState(false);
    // 🎯 [NEW] Local Pending State for UI update
    const [localPending, setLocalPending] = useState({ normal: 0, landmark: 0 });

    useEffect(() => {
        if (piUser?.uid) {
            const key = `giftbox_pending_${piUser.uid}`;
            const saved = JSON.parse(localStorage.getItem(key));
            // 🎯 [Fix] Use Unified KST Date Check
            if (saved && saved.date === getKSTTodayStr()) {
                setLocalPending({ normal: saved.normal || 0, landmark: saved.landmark || 0 });
            }
        }
    }, [piUser]);

    const calculateDistance = useCallback((lat1, lng1, lat2, lng2) => {
        const R = 6371000;
        const φ1 = lat1 * Math.PI / 180;
        const φ2 = lat2 * Math.PI / 180;
        const Δφ = (lat2 - lat1) * Math.PI / 180;
        const Δλ = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }, []);

    // 🎯 수집 로직: Client-Side Collection (LocalStorage) to reduce DB writes
    const handlePointSpotClick = useCallback(async (pointSpot) => {
        // [DEBUG] 메인넷 무반응 원인 파악용 - 확인 후 제거
        if (!myPos || !pointSpot.lat || !pointSpot.lng) {
            console.log('[TreasureHunt] early return: myPos 또는 pointSpot 좌표 없음');
            return;
        }

        const distance = calculateDistance(myPos.lat, myPos.lng, pointSpot.lat, pointSpot.lng);
        console.log('[TreasureHunt] distance:', distance.toFixed(1), 'm, myPos:', { lat: myPos.lat, lng: myPos.lng });

        if (distance > 20) {
            console.log('[TreasureHunt] early return: 거리 초과 (', distance.toFixed(1), 'm > 20m)');
            const remaining = Math.ceil(distance);
            setDistanceMessage(t(SPOT_MESSAGES.TREASURE_DISTANCE, { distance: remaining }));
            setTimeout(() => setDistanceMessage(''), 3000);
            return;
        }

        if (!piUser?.uid) {
            console.log('[TreasureHunt] early return: 로그인 필요');
            setCollectToast(t(GIFTBOX_HUB_KEYS.LOGIN_REQUIRED));
            setTimeout(() => setCollectToast(''), 3000);
            return;
        }

        if (isCollecting) {
            console.log('[TreasureHunt] early return: isCollecting=true (이전 클릭 처리 중)');
            return;
        }
        setIsCollecting(true);
        setDistanceMessage('');

        try {
            const isLandmark = !!pointSpot.isLandmarkPointSpot;
            // 🎯 [Fix] Use Unified KST Date Check
            const todayStr = getKSTTodayStr();
            const storageKey = `giftbox_pending_${piUser.uid}`;

            // 1. Get current pending from LocalStorage
            let pending = JSON.parse(localStorage.getItem(storageKey)) || { date: todayStr, normal: 0, landmark: 0 };

            // 2. Date check (Reset if new day)
            if (pending.date !== todayStr) {
                pending = { date: todayStr, normal: 0, landmark: 0 };
            }

            // 🎯 [Limit Check] Include OPENED count in the limit check!
            // Total = Inventory + Pending + Opened
            // 🎯 [Fix] pointSpotOpenedToday도 오늘 날짜인 경우만 카운트 (메인넷 stale 캐시 버그)
            const isTodayOpened = pointSpotOpenedToday?.date === todayStr;
            const currentOpenedNormal = isTodayOpened ? (pointSpotOpenedToday?.normal || 0) : 0;
            const currentOpenedLandmark = isTodayOpened ? (pointSpotOpenedToday?.landmark || 0) : 0;

            const totalNormal = (inventory?.normal || 0) + pending.normal + currentOpenedNormal;
            const totalLandmark = (inventory?.landmark || 0) + pending.landmark + currentOpenedLandmark;

            if (isLandmark) {
                if (totalLandmark >= DAILY_LANDMARK_LIMIT) {
                    console.log('[TreasureHunt] early return: 랜드마크 일일 한도', { totalLandmark, limit: DAILY_LANDMARK_LIMIT });
                    setCollectToast(t('daily_limit_reached'));
                    setTimeout(() => setCollectToast(''), 3000);
                    setIsCollecting(false);
                    return;
                }
                pending.landmark = (pending.landmark || 0) + 1;
            } else {
                if (totalNormal >= DAILY_NORMAL_LIMIT) {
                    console.log('[TreasureHunt] early return: 일반 일일 한도', { totalNormal, limit: DAILY_NORMAL_LIMIT });
                    setCollectToast(t('daily_limit_reached'));
                    setTimeout(() => setCollectToast(''), 3000);
                    setIsCollecting(false);
                    return;
                }
                pending.normal = (pending.normal || 0) + 1;
            }

            // 🎯 [Speed Check] Anti-Cheat (50km/h ≈ 13.88m/s)
            // myPos.speed is m/s
            if (myPos?.speed > 14) { // ~50.4km/h
                console.log('[TreasureHunt] early return: 속도 초과', { speed: myPos?.speed });
                setCollectToast(t(GIFTBOX_HUB_KEYS.SPEED_LIMIT_MSG));
                setTimeout(() => setCollectToast(''), 3000);
                setIsCollecting(false);
                return;
            }

            // 4. Save to LocalStorage
            localStorage.setItem(storageKey, JSON.stringify(pending));

            // 🎯 [NEW] Update Local State for immediate UI feedback
            setLocalPending({ normal: pending.normal || 0, landmark: pending.landmark || 0 });

            // 5. Update UI (Toast)
            // New Total for display
            const newTotalNormal = (inventory?.normal || 0) + pending.normal + currentOpenedNormal;
            const newTotalLandmark = (inventory?.landmark || 0) + pending.landmark + currentOpenedLandmark;

            setCollectToast(t(SPOT_MESSAGES.TREASURE_FOUND));
            setTimeout(() => setCollectToast(''), 3000);

            console.log('[TreasureHunt] 획득 성공, onPointSpotClaimed 호출', pointSpot.id);
            if (onPointSpotClaimed) onPointSpotClaimed(pointSpot.id);
            // 🎯 [Fix] 획득 후 즉시 오픈 팝업(PointSpotRewardModal) 호출 제거 → GiftBoxHub에서만 오픈

            // Trigger local inventory refresh if needed (or just rely on toast)
            // if (refreshInventory) refreshInventory(); // Not needed for local update

        } catch (err) {
            console.error('Collect failed:', err);
            setCollectToast(t('error_occurred'));
            setTimeout(() => setCollectToast(''), 3000);
        } finally {
            setIsCollecting(false);
        }
    }, [myPos, calculateDistance, piUser, isCollecting, t, inventory, pointSpotOpenedToday, onPointSpotClaimed]);

    const handleSpotClick = useCallback((spot) => {
        if (!spot) return;
        if (spot.isPointSpot) {
            handlePointSpotClick(spot);
            return;
        }
        if (onOpenDetail) onOpenDetail(spot);
    }, [handlePointSpotClick, onOpenDetail]);

    // 🎯 [Render Logic] Ensure we only show TODAY's counts
    // 🎯 [Fix] Use Unified KST Date Check
    const todayStr = getKSTTodayStr();

    // Inventory Check
    const isInventoryToday = inventory?.date === todayStr;
    const invNormal = isInventoryToday ? (inventory?.normal || 0) : 0;
    const invLandmark = isInventoryToday ? (inventory?.landmark || 0) : 0;

    // Opened Check (Assuming PointSpotOpenedToday is always valid or empty, but logic in AppContext handles it)
    // 🎯 [Fix] Date Check: Ensure stored 'openedToday' is actually for TODAY (KST)
    const isTodayOpened = pointSpotOpenedToday?.date === todayStr;
    const currentOpenedNormal = isTodayOpened ? (pointSpotOpenedToday?.normal || 0) : 0;
    const currentOpenedLandmark = isTodayOpened ? (pointSpotOpenedToday?.landmark || 0) : 0;

    // Display Logic: Inventory + Pending + Opened = Total Progress
    const displayNormal = invNormal + localPending.normal + currentOpenedNormal;
    const displayLandmark = invLandmark + localPending.landmark + currentOpenedLandmark;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2000,
                background: '#f0f0f0',
                animation: 'treasureOverlayFadeIn 0.3s ease-out'
            }}
        >
            <style>{`
        @keyframes treasureOverlayFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
            {/* 닫기 버튼 */}
            <button
                onClick={onClose}
                style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    zIndex: 2001,
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    border: 'none',
                    background: 'rgba(0, 0, 0, 0.6)',
                    backdropFilter: 'blur(8px)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.8)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                    e.currentTarget.style.transform = 'scale(1)';
                }}
            >
                <IoClose size={28} />
            </button>

            {/* 전체 화면 지도 — GPS가 준비되기 전에는 지도를 만들지 않아 서울 기본 좌표로 고정되는 것을 막는다 */}
            {myPos ? (
            <UnifiedMapView
                center={myPos}
                spots={spots}
                selectedSpotId={null}
                onSpotClick={handleSpotClick} // 🎯 [FIX] Use handleSpotClick instead of empty function
                myPos={myPos}
                isExpanded={true}
                mapTarget={mapTarget}
                useKakao={useKakaoMap}
                onToggleMap={onToggleMap}
                setOffScreenIndicator={() => { }} // 보물찾기 모드에서는 비활성화
                // 🎯 [FIX] Don't pass distanceRadius to prevent zoom override (Let mapTarget control zoom)
                distanceRadius={null}
                pointSpots={pointSpots}
                onPointSpotClick={handlePointSpotClick}
                isTreasureHuntMode={true} // 🎯 보물찾기 모드 플래그
            />
            ) : (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                    color: '#4B5563',
                    fontSize: '0.95rem',
                    textAlign: 'center',
                    padding: '0 24px'
                }}>
                    {t(SPOT_MESSAGES.LOCATION_CHECKING)}
                </div>
            )}

            {/* 🎯 내 위치로 지도 중심 이동 — 선물상자 맵 안에서만 표시 (지도 맞추기용), 하단 UI 위에 배치 */}
            {myPos && setMapTarget && (
                <button
                    type="button"
                    onClick={() => setMapTarget({
                        lat: myPos.lat,
                        lng: myPos.lng,
                        zoom: TREASURE_HUNT_ZOOM,
                        timestamp: Date.now()
                    })}
                    style={{
                        position: 'absolute',
                        bottom: '152px', // 플로팅 + 버튼(80px+56px) 위에 여유 간격으로 배치
                        right: '24px', // +버튼(56px, right 20px)과 중심축 맞춤: 20+28=48px → 48-24=24px
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'white',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 2001,
                        color: '#6C5CE7',
                        transition: 'transform 0.2s ease'
                    }}
                    aria-label={t(SPOT_MESSAGES.VIEW_ON_MAP)}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                    <IoLocate size={24} />
                </button>
            )}

            {/* 🎯 오늘 획득 개수 — "주변의 선물 상자를 찾아보세요!" 윗부분 고정 */}
            <div
                style={{
                    position: 'absolute',
                    top: '40px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(30, 41, 59, 0.92)',
                    backdropFilter: 'blur(8px)',
                    color: '#E5E7EB',
                    padding: '6px 14px',
                    borderRadius: '16px',
                    fontSize: '11px', // 🎯 [UI Fix] 폰트 사이즈 축소 (0.8rem -> 12px)
                    fontWeight: '600',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.35)',
                    zIndex: 2001,
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px', // 🎯 [UI Fix] 간격 축소 (16px -> 10px)
                    border: '1px solid rgba(148, 163, 184, 0.3)',
                    whiteSpace: 'nowrap' // 🎯 [UI Fix] 줄바꿈 방지 강제
                }}
            >
                <span>
                    {t(GIFTBOX_HUB_KEYS.NORMAL_LABEL)} {displayNormal}/{DAILY_NORMAL_LIMIT}
                </span>
                <span style={{ width: 1, height: 10, background: 'rgba(148, 163, 184, 0.5)' }} />
                <span>
                    {t(GIFTBOX_HUB_KEYS.LANDMARK_LABEL)} {displayLandmark}/{DAILY_LANDMARK_LIMIT}
                </span>
            </div>

            {/* 안내 텍스트 — 획득 개수 바로 아래 */}
            <div
                style={{
                    position: 'absolute',
                    top: '108px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(168, 85, 247, 0.9)',
                    backdropFilter: 'blur(8px)',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '24px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    zIndex: 2001,
                    pointerEvents: 'none',
                    maxWidth: '90%',
                    whiteSpace: 'nowrap'
                }}
            >
                🎁 {t(SPOT_MESSAGES.TREASURE_HINT)}
            </div>

            {/* 거리 안내 메시지 */}
            {
                distanceMessage && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '168px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(239, 68, 68, 0.95)',
                            backdropFilter: 'blur(8px)',
                            color: 'white',
                            padding: '12px 20px',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
                            zIndex: 2002,
                            animation: 'slideDown 0.3s ease-out',
                            maxWidth: '80%',
                            textAlign: 'center',
                            whiteSpace: 'normal',
                            wordBreak: 'keep-all'
                        }}
                    >
                        <style>{`
                    @keyframes slideDown {
                        from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
                        to { opacity: 1; transform: translateX(-50%) translateY(0); }
                    }
                `}</style>
                        📍 {distanceMessage}
                    </div>
                )
            }
            {
                collectToast && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '168px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(168, 85, 247, 0.95)',
                            backdropFilter: 'blur(8px)',
                            color: 'white',
                            padding: '12px 20px',
                            borderRadius: '20px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
                            zIndex: 2002,
                            maxWidth: '85%',
                            textAlign: 'center'
                        }}
                    >
                        🎁 {collectToast}
                    </div>
                )
            }
        </div >
    );
};

export default TreasureHuntOverlay;
